import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import {
  ChangeEventType,
  GeolocationLocationSetData,
  IGeolocationSet,
} from '../../../types';
import { DEFAULT_COLOR_THEME, ScrollStyle } from '../../../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FacebookMap from '../Assetz/Facebook/GeolocationSet/FacebookMap';
import GeolocationList from '../Assetz/Facebook/GeolocationSet/GeolocationList';
import {
  humanizeLocationTitle,
  isEmptyGeolocations,
} from '../../../utils/helpers/AssetHelpers';
import Spinner from '../../Spinner';
import { numberShortener } from '../../../utils/numberFormatter';
import { PrimaryButton } from '../../Buttons';
import AddIcon from '@mui/icons-material/Add';

interface DiyGeolocationSetFormProps {
  geolocationSets: IGeolocationSet[];
  filteredGeolocationSets: IGeolocationSet[];
  setFilteredGeolocationSets: Dispatch<SetStateAction<IGeolocationSet[]>>;
  onSelectGeolocationSet: (geolocationSet: IGeolocationSet) => void;
  selectedGeolocationSet: IGeolocationSet;
  setSelectedGeolocationSet: Dispatch<SetStateAction<IGeolocationSet>>;
  reachError: string;
  reachLoading: boolean;
  reachEstimate: any;
  setOpenGeolocationSetForm: Dispatch<SetStateAction<boolean>>;
  setGeolocationSetAction: Dispatch<SetStateAction<'create' | 'edit'>>;
  loading: boolean;
}

const DiyGeolocationSetForm: FC<DiyGeolocationSetFormProps> = ({
  geolocationSets,
  filteredGeolocationSets,
  setFilteredGeolocationSets,
  onSelectGeolocationSet,
  selectedGeolocationSet,
  setSelectedGeolocationSet,
  reachError,
  reachLoading,
  reachEstimate,
  setOpenGeolocationSetForm,
  setGeolocationSetAction,
  loading,
}) => {
  const theme = useTheme();
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));
  const [search, setSearch] = useState<string>('');

  const handleOnSearch = (e: ChangeEventType) => {
    setSearch(e.target.value);
    const temp = geolocationSets.filter((geolocationSet: IGeolocationSet) =>
      geolocationSet.name.toLowerCase().includes(e.target.value.toLowerCase()),
    );

    setFilteredGeolocationSets(temp);
  };

  const handleOnClickCreate = () => {
    setOpenGeolocationSetForm(true);
    setSelectedGeolocationSet(null);
    setGeolocationSetAction('create');
  };

  const handleOnClickEdit = (golocationSet: IGeolocationSet) => {
    setOpenGeolocationSetForm(true);
    setSelectedGeolocationSet(golocationSet);
    setGeolocationSetAction('edit');
  };

  const scrollStyling = {
    minHeight: `calc(100vh - 250px)`,
    maxHeight: `calc(100vh - 250px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...ScrollStyle(),
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4} sx={!xsOnly ? scrollStyling : {}}>
        <Grid
          container
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 999,
            backgroundColor: 'white',
            mb: 2,
          }}
        >
          <Grid item xs={12}>
            <PrimaryButton
              title="Add Geolocation Set"
              type="button"
              handleOnClick={handleOnClickCreate}
              startIcon={<AddIcon />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="text"
              id="search"
              name="search"
              label="Search"
              onChange={handleOnSearch}
              value={search}
              InputLabelProps={{ shrink: true }}
              size="small"
              placeholder="Search by geo locations set name"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ px: 1, paddingY: '10px' }}>
          {!loading ? (
            filteredGeolocationSets?.length > 0 ? (
              filteredGeolocationSets.map((geolocationSet: IGeolocationSet) => {
                const selected =
                  selectedGeolocationSet?._id === geolocationSet._id;

                return (
                  <Grid
                    item
                    xs={12}
                    key={geolocationSet._id}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    {selected ? (
                      <Box
                        component="div"
                        sx={{
                          position: 'absolute',
                          top: '8px',
                          zIndex: 1,
                          right: '-10px',
                          float: 'right',
                          margin: '1px',
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            color: DEFAULT_COLOR_THEME,
                            bgcolor: 'InfoBackground',
                            borderRadius: '50%',
                          }}
                        />
                      </Box>
                    ) : null}

                    <Card
                      sx={{
                        height: '100%',
                        border: selected ? 'solid' : '',
                        borderColor: selected ? DEFAULT_COLOR_THEME : '',
                        borderWidth: 'medium',
                        zIndex: 0,
                      }}
                    >
                      <CardActionArea
                        onClick={() => onSelectGeolocationSet(geolocationSet)}
                      >
                        <CardHeader
                          title={geolocationSet.name}
                          subheader={`${
                            geolocationSet.description
                          }\nEst. Audience: ${numberShortener(
                            geolocationSet.minReach,
                          )} - ${numberShortener(geolocationSet.maxReach)}`}
                          titleTypographyProps={{
                            variant: 'body1',
                            sx: { fontWeight: 'bold' },
                          }}
                          subheaderTypographyProps={{
                            variant: 'body2',
                            sx: { whiteSpace: 'pre-wrap' },
                          }}
                        />
                      </CardActionArea>

                      <CardActions
                        sx={{ justifyContent: 'flex-end', display: 'flex' }}
                      >
                        <PrimaryButton
                          title="Edit"
                          type="button"
                          variant="text"
                          handleOnClick={() =>
                            handleOnClickEdit(geolocationSet)
                          }
                        />
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                mt={2}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', textAlign: 'center' }}
                >
                  No data to show
                </Typography>

                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                  Click Add Geolocation Set to create a new one
                </Typography>
              </Grid>
            )
          ) : (
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
              mt={2}
            >
              <Spinner />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} sm={8} sx={!xsOnly ? scrollStyling : {}}>
        <Grid container spacing={2} sx={{ px: 1, paddingY: '10px' }}>
          <Grid item xs={12} sx={{ flexDirection: 'column', display: 'flex' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Estimated Audience Size:{' '}
              <Typography
                component="span"
                variant="body1"
                sx={{ marginLeft: '10px' }}
              >
                {reachLoading ? (
                  <Spinner />
                ) : (
                  `${numberShortener(
                    reachEstimate.users_lower_bound,
                  )} - ${numberShortener(reachEstimate.users_upper_bound)}`
                )}
              </Typography>
            </Typography>

            {reachError ? (
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', color: 'red' }}
              >
                {reachError}
              </Typography>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <FacebookMap
              geolocations={selectedGeolocationSet?.geolocations}
              excludedGeolocations={
                selectedGeolocationSet?.excludedGeolocations
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                marginRight: '10px',
                textAlign: 'center',
              }}
            >
              Included
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  {!isEmptyGeolocations(
                    selectedGeolocationSet?.geolocations,
                  ) ? (
                    Object?.keys(
                      selectedGeolocationSet?.geolocations || {},
                    )?.map((key: string, index: number) => {
                      if (
                        selectedGeolocationSet?.geolocations?.[key]?.length ===
                        0
                      )
                        return <Box key={`${key}-${index}`} />;

                      return (
                        <Grid item xs={12} key={`${key}-${index}`}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold' }}
                              >
                                {humanizeLocationTitle(key)}
                              </Typography>
                            </Grid>

                            {selectedGeolocationSet?.geolocations?.[key]?.map(
                              (geolocation: GeolocationLocationSetData) => {
                                return (
                                  <Grid item xs={12} key={geolocation.key}>
                                    <GeolocationList
                                      geolocation={geolocation}
                                    />
                                  </Grid>
                                );
                              },
                            )}
                          </Grid>
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', textAlign: 'center' }}
                      >
                        No Locations Added
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                marginRight: '10px',
                textAlign: 'center',
              }}
            >
              Excluded
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  {!isEmptyGeolocations(
                    selectedGeolocationSet?.excludedGeolocations,
                  ) ? (
                    Object.keys(
                      selectedGeolocationSet?.excludedGeolocations,
                    )?.map((key: string, index: number) => {
                      if (
                        selectedGeolocationSet?.excludedGeolocations?.[key]
                          ?.length === 0
                      )
                        return <Box key={`${key}-${index}`} />;

                      return (
                        <Grid item xs={12} key={`${key}-${index}`}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold' }}
                              >
                                {humanizeLocationTitle(key)}
                              </Typography>
                            </Grid>

                            {selectedGeolocationSet?.excludedGeolocations?.[
                              key
                            ]?.map(
                              (geolocation: GeolocationLocationSetData) => {
                                return (
                                  <Grid item xs={12} key={geolocation.key}>
                                    <GeolocationList
                                      exclude
                                      geolocation={geolocation}
                                    />
                                  </Grid>
                                );
                              },
                            )}
                          </Grid>
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', textAlign: 'center' }}
                      >
                        No Locations Added
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiyGeolocationSetForm;
